import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, FileDown, Loader2, AlertCircle, Calendar, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../lib/logger';

/**
 * Public, unauthenticated landing page for shared Quarterly Decks.
 *
 * Recipients (CFOs, board members) click an emailed `/q/<token>` link
 * and land here. The page fetches lightweight metadata first
 * (`/api/quarterly-deck-public/<token>`) so we can render an instant
 * branded skeleton, then offers a single CTA that streams the live
 * PDF (`/api/quarterly-deck-public/<token>/pdf`). Every CTA click
 * increments the owner-visible view counter and pushes a
 * timestamp+UA row onto the token's `views` array — that's how we
 * power the "CFO opened the deck twice last week" panel back in the
 * authenticated app.
 */
export default function QuarterlyDeckPublicPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/quarterly-deck-public/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setError({ status: res.status, detail: body.detail || 'Could not load deck' });
          }
          return;
        }
        const data = await res.json();
        if (!cancelled) setMeta(data);
      } catch (err) {
        logger.error?.('public deck meta fetch failed', err);
        if (!cancelled) setError({ status: 0, detail: 'Network error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, token]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${apiBase}/api/quarterly-deck-public/${token}/pdf`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Could not download deck');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : 'Quarterly_Impact_Deck.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Deck downloaded');
      // Refresh meta so the "viewed N times" counter updates without
      // forcing the recipient to reload the page.
      try {
        const m2 = await fetch(`${apiBase}/api/quarterly-deck-public/${token}`);
        if (m2.ok) setMeta(await m2.json());
      } catch (_e) { /* non-fatal */ }
    } catch (err) {
      logger.error?.('public deck download failed', err);
      toast.error('Could not download deck');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    const isExpired = error.status === 410;
    const isMissing = error.status === 404;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-rose-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-rose-200" data-testid="public-deck-error">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">
              {isExpired ? 'This link has expired' : (isMissing ? 'Link not found' : 'Could not load deck')}
            </h1>
            <p className="text-sm text-slate-600">
              {isExpired
                ? 'Reach out to the sender for a fresh link.'
                : (isMissing
                    ? 'Double-check the URL or ask the sender to resend.'
                    : (error.detail || 'Please try again later.'))}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center px-4 py-12" data-testid="public-deck-page">
      <div className="max-w-xl w-full">
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 px-8 py-10 text-white relative">
            <div
              className="absolute inset-0 opacity-15 pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 15% 30%, #ffffff 0%, transparent 35%), radial-gradient(circle at 90% 70%, #ffffff 0%, transparent 35%)',
              }}
              aria-hidden="true"
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-slate-700" />
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-700">
                  Shared Quarterly Deck
                </span>
              </div>
              <h1 className="text-3xl font-bold leading-tight" data-testid="public-deck-title">
                {meta.organization_name || 'Quarterly Impact Deck'}
              </h1>
              <p className="text-slate-700 text-sm mt-2">
                {meta.quarter_label} · {meta.pin_count} pinned tile{meta.pin_count === 1 ? '' : 's'}
                {meta.owner_display_name ? ` · curated by ${meta.owner_display_name}` : ''}
              </p>
            </div>
          </div>
          <CardContent className="px-8 py-7 bg-white">
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                You&rsquo;re viewing a read-only Quarterly Impact Deck shared with you by{' '}
                <span className="font-semibold text-slate-900">
                  {meta.owner_display_name || 'the team'}
                </span>
                {meta.recipient_label ? ` (sent to ${meta.recipient_label})` : ''}.
              </p>
              <p className="text-slate-500 text-xs">
                The deck reflects live numbers at the time you open it &mdash; so refreshing this page
                tomorrow will show whatever has moved between now and then.
              </p>
              {meta.organization_name && (
                <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t">
                  <Building2 className="h-3.5 w-3.5" />
                  <span>{meta.organization_name}</span>
                </div>
              )}
              {meta.expires_at && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    Available until{' '}
                    {new Date(meta.expires_at).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </div>
            <Button
              onClick={handleDownload}
              disabled={downloading || meta.pin_count === 0}
              className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="public-deck-download-btn"
            >
              {downloading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating PDF…</>
              ) : (
                <><FileDown className="h-4 w-4 mr-2" /> Download Deck (PDF)</>
              )}
            </Button>
            {meta.view_count > 0 && (
              <p className="text-center text-xs text-slate-400 mt-3">
                Opened {meta.view_count} time{meta.view_count === 1 ? '' : 's'}
              </p>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Seertech
        </p>
      </div>
    </div>
  );
}
